import { ChartFrame } from '../components/charts/ChartFrame';
import { TrendChart } from '../components/charts/TrendChart';
import { BarSeries } from '../components/charts/BarSeries';
import { Meter } from '../components/tiles/Meter';
import { StatTile } from '../components/tiles/StatTile';
import { IconCritical, IconGood, IconWarning } from '../components/Icons';
import { full, longDate, percent, relativeDays } from '../lib/format';
import { PLATFORM_COLORS, platformSeries } from '../lib/platform';
import { PLATFORM_LABELS, platformsFor } from '../data/types';
import type { ErrorRow, PlatformFilter, Snapshot } from '../data/types';

interface Props {
  snap: Snapshot;
  filter: PlatformFilter;
}

/** Status always ships icon + label — never color alone. */
function Severity({ level }: { level: ErrorRow['severity'] }) {
  const map = {
    critical: { Icon: IconCritical, label: 'Critical' },
    serious: { Icon: IconWarning, label: 'Serious' },
    warning: { Icon: IconWarning, label: 'Warning' },
  } as const;
  const { Icon, label } = map[level];
  return (
    <span className={`sev sev--${level}`}>
      <Icon size={13} />
      {label}
    </span>
  );
}

export function Stability({ snap, filter }: Props) {
  const series = platformSeries(filter);
  const active = platformsFor(filter);
  const activeSet = new Set<string>(active);

  const errors = snap.errors.filter((e) => activeSet.has(e.platform));

  // Crash-free rate per build is the table that actually drives a rollback
  // decision, so it leads the page.
  const versionHealth = snap.versions
    .filter((v) => activeSet.has(v.platform))
    .sort((a, b) => a.crashFreeRate - b.crashFreeRate)
    .slice(0, 10);

  const crashSeries = snap.activeUsers.dau.map((d, i) => {
    const hist = snap.summary.crashFreeRate.history;
    const v = hist[Math.min(hist.length - 1, i)] ?? snap.summary.crashFreeRate.value;
    return { date: d.date, rate: v * 100 };
  });

  const severities: ErrorRow['severity'][] = ['critical', 'serious', 'warning'];
  const errorsBySeverity = severities.map((sev) => {
    const row: Record<string, string | number> = {
      name: sev.charAt(0).toUpperCase() + sev.slice(1),
    };
    for (const p of active) {
      row[p] = snap.errors.filter((e) => e.platform === p && e.severity === sev).length;
    }
    return row;
  });

  const totalEvents = errors.reduce((a, e) => a + e.events, 0);
  const affectedUsers = errors.reduce((a, e) => a + e.users, 0);
  const criticalCount = errors.filter((e) => e.severity === 'critical').length;

  return (
    <>
      <div className="grid" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
        <StatTile
          label="Crash-free sessions"
          value={percent(snap.summary.crashFreeRate.value, 2)}
          delta={snap.summary.crashFreeRate.delta}
          history={snap.summary.crashFreeRate.history}
          sparkColor="var(--status-good)"
        />
        <StatTile label="Open issues" value={String(errors.length)} foot={`${criticalCount} critical`} />
        <StatTile label="Error events" value={full(totalEvents)} foot="in range" />
        <StatTile label="Users affected" value={full(affectedUsers)} foot="unique, in range" />
      </div>

      <div className="grid">
        <div data-span style={{ gridColumn: 'span 7' }}>
          <ChartFrame
            title="Crash-free session rate"
            subtitle="Fleet-wide, against a 99.5% floor"
            table={{
              columns: ['Date', 'Crash-free rate'],
              rows: crashSeries.map((c) => [longDate(c.date), `${c.rate.toFixed(2)}%`]),
            }}
          >
            <TrendChart
              data={crashSeries}
              series={[{ key: 'rate', label: 'Crash-free rate', color: 'var(--status-good)' }]}
              valueFormat={(n) => `${n.toFixed(2)}%`}
              // Auto-fitting a rate that only moves within 0.1% zooms so hard
              // that ordinary noise looks like a crisis. Pin the scale to the
              // band anyone actually cares about and mark the floor.
              height={300}
              yDomain={[99, 100]}
              yWidth={58}
              reference={{ y: 99.5, label: '99.5% floor' }}
            />
          </ChartFrame>
        </div>

        <div data-span style={{ gridColumn: 'span 5' }}>
          <section className="card">
            <header className="card__head">
              <div className="card__titles">
                <h2 className="card__title">Performance budgets</h2>
                <p className="card__sub">
                  {active.length === 1
                    ? `${PLATFORM_LABELS[active[0]]} median against the team's target`
                    : 'Median across the selected platforms, against the target'}
                </p>
              </div>
            </header>
            <div className="card__body">
              <div className="meter-list">
                {snap.perf.map((p) => {
                  const value =
                    active.reduce((sum, plat) => sum + p.byPlatform[plat], 0) / (active.length || 1);
                  return (
                    <Meter
                      key={p.name}
                      label={p.name}
                      display={`${Math.round(value)}${p.unit}`}
                      value={value}
                      budget={p.budget}
                      budgetLabel={`${p.budget}${p.unit}`}
                    />
                  );
                })}
              </div>
            </div>
          </section>
        </div>

        <div data-span style={{ gridColumn: 'span 5' }}>
          <ChartFrame
            title="Open issues by severity"
            subtitle="Count of distinct issues"
            table={{
              columns: ['Severity', ...active.map((p) => PLATFORM_LABELS[p])],
              rows: errorsBySeverity.map((e) => [String(e.name), ...active.map((p) => String(e[p]))]),
            }}
          >
            <BarSeries
              data={errorsBySeverity}
              categoryKey="name"
              series={series}
              height={240}
              valueFormat={(n) => String(Math.round(n))}
              allowDecimals={false}
            />
          </ChartFrame>
        </div>

        <div data-span style={{ gridColumn: 'span 7' }}>
          <ChartFrame
            title="Least healthy builds"
            subtitle="Sorted by crash-free rate, worst first"
            table={{
              columns: ['Version', 'Platform', 'Users', 'Crash-free'],
              rows: versionHealth.map((v) => [
                v.version,
                PLATFORM_LABELS[v.platform],
                full(v.users),
                percent(v.crashFreeRate, 2),
              ]),
            }}
          >
            <div className="rank">
              {versionHealth.map((v) => {
                const healthy = v.crashFreeRate >= 0.995;
                return (
                  <div className="rank__row" key={`${v.platform}-${v.version}`}>
                    <span className="rank__name">
                      <span className="dot" style={{ background: PLATFORM_COLORS[v.platform] }} />
                      <span className="mono">{v.version}</span>
                      <span className="muted" style={{ fontSize: 11 }}>
                        {PLATFORM_LABELS[v.platform]}
                      </span>
                    </span>
                    <span className={`sev sev--${healthy ? 'good' : 'serious'}`}>
                      {healthy ? <IconGood size={12} /> : <IconWarning size={12} />}
                      {percent(v.crashFreeRate, 2)}
                    </span>
                    <div className="rank__bar bar-track">
                      <div
                        className="bar-fill"
                        style={{
                          // Zoomed to the 98–100% band, which is where every
                          // shipped build actually sits. A 99% floor clamped
                          // the older builds to a stub with no visible spread.
                          width: `${Math.min(100, Math.max(2, ((v.crashFreeRate - 0.98) / 0.02) * 100))}%`,
                          background: healthy ? 'var(--status-good)' : 'var(--status-serious)',
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="card__sub" style={{ marginTop: 12 }}>
              Bars are scaled across 98–100%. Anything under 99.5% is over budget.
            </p>
          </ChartFrame>
        </div>

        <div data-span style={{ gridColumn: 'span 12' }}>
          <section className="card">
            <header className="card__head">
              <div className="card__titles">
                <h2 className="card__title">Top issues</h2>
                <p className="card__sub">Ranked by event volume in the selected range</p>
              </div>
            </header>
            <div className="card__body">
              {errors.length === 0 ? (
                <p className="empty">No open issues on the selected platforms.</p>
              ) : (
                <div className="table-scroll" style={{ maxHeight: 400 }}>
                  <table className="data">
                    <thead>
                      <tr>
                        <th scope="col">Issue</th>
                        <th scope="col" style={{ textAlign: 'left' }}>
                          Severity
                        </th>
                        <th scope="col" style={{ textAlign: 'left' }}>
                          Platform
                        </th>
                        <th scope="col">Events</th>
                        <th scope="col">Users</th>
                        <th scope="col" style={{ textAlign: 'left' }}>
                          Version
                        </th>
                        <th scope="col" style={{ textAlign: 'left' }}>
                          Last seen
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {errors.map((e) => (
                        <tr key={e.id}>
                          <th scope="row" style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                            <span className="mono muted">{e.id}</span>{' '}
                            <span className="truncate" title={e.title}>
                              {e.title}
                            </span>
                          </th>
                          <td style={{ textAlign: 'left' }}>
                            <Severity level={e.severity} />
                          </td>
                          <td style={{ textAlign: 'left' }}>
                            <span className="rank__name">
                              <span className="dot" style={{ background: PLATFORM_COLORS[e.platform] }} />
                              {PLATFORM_LABELS[e.platform]}
                            </span>
                          </td>
                          <td>{full(e.events)}</td>
                          <td>{full(e.users)}</td>
                          <td style={{ textAlign: 'left' }} className="mono">
                            {e.version}
                          </td>
                          <td style={{ textAlign: 'left' }} className="muted">
                            {relativeDays(e.lastSeen)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </>
  );
}
